import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {GetAnalysisScratchFile} from 'analysis-tool/AnalysisTypes';
import {FONT_FAMILY_MONOSPACED} from 'styles/FontConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {GRAY_LIGHT, GRAY_LIGHTER, NEAR_BLACK} from 'ui-library/Colors';
import MarkdownContent from 'ui-library/MarkdownContent';

import ExternalCSVFile from './ExternalCSVFile';
import ExternalFileChip from './ExternalFileChip';
import ExternalFileIFrame from './ExternalFileIFrame';
import ExternalFileImage from './ExternalFileImage';
import ExternalFileRenderer from './ExternalFileRenderer';

interface ExternalFilePaneContentProps {
  scratchFile: GetAnalysisScratchFile;
}

export function renderFilePreview(scratchFile: GetAnalysisScratchFile): JSX.Element | null {
  switch (scratchFile.mimeType) {
    // In the future, we could support `HTML` as a first-class `EXTERNAL_FILE_TYPE`, so that embedded HTML
    // previews are supported across the whole platform (i.e., for all `ExternalFile` callers). For now, we
    // scope it down to Analyses.
    case 'text/html':
      return (
        <ExternalFileIFrame
          scratchFile={scratchFile}
          externalFileWarning="This block contains a file that is external to Benchling."
          // We allow scripts in the iframe because they are needed to render HTML reports generated by JMP.
          dangerouslyAllowScriptsInIFrame={true}
          // File ExternalFiles do not have their Content-Type metadata set, so they default to
          // application/octet-stream. Therefore, we need to tell the S3 signing endpoint to override the
          // Content-Type header to text/html.
          dangerouslyOverrideHTMLFileContentTypeHeader={true}
        />
      );
    case 'application/json':
    case 'text/plain':
      return (
        <ExternalFileRenderer scratchFile={scratchFile} fileType="text">
          {(content) => (
            <textarea
              className={css(styles.textPreview)}
              value={content}
              readOnly={true}
              placeholder="Empty file"
            />
          )}
        </ExternalFileRenderer>
      );
    case 'text/markdown':
      return (
        <ExternalFileRenderer scratchFile={scratchFile} fileType="markdown">
          {(content) => (
            <div className={css(styles.markdownPreview)}>
              <MarkdownContent content={content} />
            </div>
          )}
        </ExternalFileRenderer>
      );
    case 'text/csv':
    case 'text/tsv':
      return <ExternalCSVFile scratchFile={scratchFile} />;
    case 'image/jpeg':
    case 'image/png':
      return <ExternalFileImage scratchFile={scratchFile} />;
    default:
      return null;
  }
}

/**
 * Render `Pane` content for the given `ExternalFile` for Analysis use cases
 *
 * As of writing, those use cases are:
 *   - Embedded HTML for HTML reports
 *   - Text files for plain text content (.txt, .rtf files)
 *   - Markdown files for markdown content (.md files)
 *   - CSV files for CSV content (.csv, .tsv files)
 *   - File chip for JMP and JRP files
 *
 * This component is intentionally written to be `File`-agnostic, so that it may share code with other
 * `ExternalFile` callers more easily in the future.
 */
export default function ExternalFilePaneContent({scratchFile}: ExternalFilePaneContentProps): JSX.Element {
  const filePreview = renderFilePreview(scratchFile);
  return filePreview ?? <ExternalFileChip scratchFile={scratchFile} />;
}

const styles = StyleSheet.create({
  textPreview: {
    ...FONT_PROXIMA_13,
    width: '100%',
    height: '100%',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    resize: 'none',
    padding: 12,
    fontFamily: FONT_FAMILY_MONOSPACED,
    backgroundColor: GRAY_LIGHTER,
    color: NEAR_BLACK,
  },
  markdownPreview: {
    ...FONT_PROXIMA_13,
  },
});
